/**
 * NotificationSettings.js
 * Configure SMTP for monthly email reports and zero-day alerts.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { api } from '../api';

export default function NotificationSettings() {
  const [settings, setSettings] = useState({
    smtpHost: '', smtpPort: '587', smtpUser: '', smtpPass: '',
    smtpFrom: '', smtpTo: '', monthlyReport: true, reportDay: '1'
  });
  const [loading,  setLoading]  = useState(true);
  const [saving,   setSaving]   = useState(false);
  const [testing,  setTesting]  = useState(false);
  const [sending,  setSending]  = useState(false);
  const [msg,      setMsg]      = useState({ text: '', type: '' });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.getNotificationSettings();
      setSettings(s => ({ ...s, ...r.data }));
    } catch (e) { flash('Failed to load settings: ' + e.message, 'error'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  function flash(text, type = 'success') {
    setMsg({ text, type });
    setTimeout(() => setMsg({ text: '', type: '' }), 5000);
  }

  async function save() {
    setSaving(true);
    try {
      await api.saveNotificationSettings(settings);
      flash('✔ Settings saved');
    } catch (e) { flash('Save failed: ' + e.message, 'error'); }
    finally { setSaving(false); }
  }

  async function testConnection() {
    setTesting(true);
    try {
      const r = await api.testSmtp();
      if (r.data.ok) {
        flash('✔ SMTP connection successful — ready to send emails');
      } else {
        const errMsg = r.data.error || 'Connection failed';
        const hint = r.data.hint ? `\n\nHint: ${r.data.hint}` : '';
        flash(`SMTP test failed: ${errMsg}${hint}`, 'error');
      }
    } catch (e) { flash('Test failed: ' + (e.response?.data?.error || e.message), 'error'); }
    finally { setTesting(false); }
  }

  async function sendNow() {
    setSending(true);
    try {
      const r = await api.sendMonthlyReport();
      if (r.data.ok) flash(`✔ Monthly report sent to ${r.data.results?.map(x=>x.to).join(', ')}`);
      else flash('Send failed: ' + r.data.error, 'error');
    } catch (e) { flash('Send failed: ' + (e.response?.data?.error || e.message), 'error'); }
    finally { setSending(false); }
  }

  const set = (key, val) => setSettings(s => ({ ...s, [key]: val }));

  return (
    <div>
      <div className="page-header">
        <div>
          <div className="page-title">📧 Notification Settings</div>
          <div className="page-subtitle">
            Configure email reports · Monthly patch summary · Zero-day alerts
          </div>
        </div>
      </div>

      {msg.text && (
        <div className={`alert ${msg.type === 'error' ? 'error' : 'success'}`}
          style={{ marginBottom: 16 }}>{msg.text}</div>
      )}

      {loading ? <div className="loading">Loading...</div> : (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>

          {/* SMTP Config */}
          <div className="panel" style={{ padding: 24 }}>
            <div style={{ fontSize: 14, fontWeight: 700, color: '#1e2a3b', marginBottom: 20 }}>
              📨 SMTP Configuration
            </div>

            <div className="form-group">
              <label className="form-label">SMTP Host</label>
              <input className="form-input" value={settings.smtpHost}
                onChange={e => set('smtpHost', e.target.value)}
                placeholder="smtp.gmail.com / smtp.office365.com" />
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
              <div className="form-group">
                <label className="form-label">Port</label>
                <select className="form-input" value={settings.smtpPort}
                  onChange={e => set('smtpPort', e.target.value)}>
                  <option value="587">587 (STARTTLS)</option>
                  <option value="465">465 (SSL)</option>
                  <option value="25">25 (SMTP)</option>
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">From Address</label>
                <input className="form-input" value={settings.smtpFrom}
                  onChange={e => set('smtpFrom', e.target.value)}
                  placeholder="noreply@covasant.com" />
              </div>
            </div>

            <div className="form-group">
              <label className="form-label">SMTP Username</label>
              <input className="form-input" value={settings.smtpUser}
                onChange={e => set('smtpUser', e.target.value)}
                placeholder="your@email.com" autoComplete="off" />
            </div>

            <div className="form-group">
              <label className="form-label">SMTP Password {settings.smtpPassSet && '(set — leave blank to keep)'}</label>
              <input className="form-input" type="password" value={settings.smtpPass}
                onChange={e => set('smtpPass', e.target.value)}
                placeholder={settings.smtpPassSet ? '••••••••' : 'Enter password or app password'}
                autoComplete="new-password" />
              {settings.smtpHost?.includes('gmail') && (
                <div style={{ fontSize: 11, color: '#d97706', marginTop: 4, background: '#fffbeb',
                  border: '1px solid #fde68a', borderRadius: 6, padding: '6px 10px' }}>
                  ⚠ <strong>Gmail requires an App Password</strong> — your regular password won't work.<br/>
                  Go to: <a href="https://myaccount.google.com/apppasswords" target="_blank" rel="noreferrer"
                    style={{ color: '#1a56db' }}>myaccount.google.com/apppasswords</a> →
                  Create app password → Copy the 16-character code → paste here.
                  <br/><em>Requires 2-Step Verification to be enabled on your Google account.</em>
                </div>
              )}
            </div>

            <div className="form-group">
              <label className="form-label">Send Reports To (comma-separated)</label>
              <input className="form-input" value={settings.smtpTo}
                onChange={e => set('smtpTo', e.target.value)}
                placeholder="admin@covasant.com, manager@covasant.com" />
            </div>

            <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
              <button className="btn btn-primary" onClick={save} disabled={saving}>
                {saving ? '⟳ Saving...' : 'Save Settings'}
              </button>
              <button className="btn btn-outline" onClick={testConnection} disabled={testing}>
                {testing ? '⟳ Testing...' : '🔌 Test Connection'}
              </button>
            </div>
          </div>

          {/* Report Schedule */}
          <div className="panel" style={{ padding: 24 }}>
            <div style={{ fontSize: 14, fontWeight: 700, color: '#1e2a3b', marginBottom: 20 }}>
              📅 Monthly Report Schedule
            </div>

            <div style={{ background: '#f8fafc', borderRadius: 8, padding: 16,
              border: '1px solid #e8ecf4', marginBottom: 20 }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: '#1e2a3b', marginBottom: 8 }}>
                What's included in the monthly report:
              </div>
              <ul style={{ fontSize: 12, color: '#64748b', margin: 0, paddingLeft: 18, lineHeight: 1.8 }}>
                <li>Total endpoints online/offline</li>
                <li>Compliance % and patch success rate</li>
                <li>Failed/pending patch counts</li>
                <li>Rollback and emergency deployment counts</li>
                <li>New patch releases this month</li>
                <li>End-of-Life alerts</li>
                <li>Top risk devices</li>
              </ul>
            </div>

            <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', marginBottom: 16 }}>
              <input type="checkbox" checked={settings.monthlyReport}
                onChange={e => set('monthlyReport', e.target.checked)} />
              <span style={{ fontSize: 13, fontWeight: 500 }}>Enable monthly email reports</span>
            </label>

            <div className="form-group">
              <label className="form-label">Send on day of month</label>
              <select className="form-input" style={{ maxWidth: 160 }}
                value={settings.reportDay}
                onChange={e => set('reportDay', e.target.value)}
                disabled={!settings.monthlyReport}>
                {Array.from({ length: 28 }, (_, i) => i + 1).map(d => (
                  <option key={d} value={d}>{d}{d===1?'st':d===2?'nd':d===3?'rd':'th'} of each month</option>
                ))}
              </select>
              <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 4 }}>
                Reports are sent on the first auto-fetch cycle of this day
              </div>
            </div>

            <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
              <button className="btn btn-primary" onClick={save} disabled={saving}>
                {saving ? '⟳ Saving...' : 'Save Schedule'}
              </button>
              <button className="btn btn-outline" onClick={sendNow} disabled={sending}
                title="Send a test report now to all recipients">
                {sending ? '⟳ Sending...' : '📧 Send Report Now'}
              </button>
            </div>
          </div>

          {/* Security hardening status */}
          <div className="panel" style={{ padding: 24, gridColumn: '1/-1' }}>
            <div style={{ fontSize: 14, fontWeight: 700, color: '#1e2a3b', marginBottom: 16 }}>
              🔒 Security Hardening Status (Pre-VAPT Checklist)
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 12 }}>
              {[
                { label: 'HTTPS (Let\'s Encrypt)', status: true,  detail: 'patchsphere.covasant.com:3030' },
                { label: 'JWT Authentication',     status: true,  detail: '8-hour token expiry' },
                { label: 'bcrypt Password Hashing',status: true,  detail: 'bcrypt rounds=10' },
                { label: 'Rate Limiting (Login)',   status: true,  detail: '5 attempts/minute per IP' },
                { label: 'Role-Based Access',       status: true,  detail: 'admin/operator/viewer' },
                { label: 'Audit Logging',           status: true,  detail: '7-year retention' },
                { label: 'DB Column Encryption',    status: true,  detail: 'AES-256-GCM — device API keys encrypted' },
                { label: 'VAPT Assessment',         status: false, detail: 'Scheduled — post stable deployment' },
              ].map((item, i) => (
                <div key={i} style={{
                  background: item.status ? '#f0fdf4' : '#f8fafc',
                  border: `1px solid ${item.status ? '#bbf7d0' : '#e8ecf4'}`,
                  borderRadius: 8, padding: '12px 14px'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
                    <span style={{ fontSize: 14 }}>{item.status ? '✅' : '⏳'}</span>
                    <span style={{ fontSize: 12, fontWeight: 700,
                      color: item.status ? '#16a34a' : '#94a3b8' }}>
                      {item.status ? 'Implemented' : 'Pending'}
                    </span>
                  </div>
                  <div style={{ fontSize: 12, fontWeight: 600, color: '#1e2a3b' }}>{item.label}</div>
                  <div style={{ fontSize: 10, color: '#94a3b8', marginTop: 2 }}>{item.detail}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
